import { useMemo, useCallback, useState } from 'react'
import { Outlet, useNavigate, useParams } from 'react-router-dom-v5-compat'

import { useQuery } from '@apollo/client'

import {
  Button,
  CompassIcon,
  DropdownMenu,
  MessageCircleHeartIcon,
  PlusIcon,
  toast,
  useIsMobileScreenSize,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact, Client, Product } from '@buffer-mono/tracking-plan'

import { FeedbackWidget } from '~publish/components/FeedbackWidget'
import { PageLayout } from '~publish/components/PageLayout'
import {
  useAccount,
  useHasPaymentDetails,
  useIsOneBufferOrg,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { graphql, readFragment } from '~publish/graphql'
import { range } from '~publish/helpers/range'
import { isOfType } from '~publish/helpers/typeGuards'
import { isFreeUser } from '~publish/helpers/user'
import { useInMemoryRestoreScroll } from '~publish/hooks/scroll/useRestoreScroll'
import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'
import { usePageTitle } from '~publish/hooks/usePageTitle'
import { navigateToMigrationHub } from '~publish/legacy/upgrade-paths/helpersUpgradePaths'
import { isNotUndefined } from '~publish/legacy/utils/isNotUndefined/isNotUndefined'

import { CollectionListEmptyState } from './CollectionListEmptyState'
import { Limits, type FeedCTA } from '../../constants/constants'
import {
  parseErrorMessage,
  useCreateFeed,
  type WrappedCreateFeedResponse,
} from '../../hooks/useCreateFeed'
import { useDeleteFeed } from '../../hooks/useDeleteFeed'
import { useFeedItems } from '../../hooks/useFeedItems'
import { useFeedsForSidebar } from '../../hooks/useFeedsForSidebar'
import { useRefreshFeedCollection } from '../../hooks/useRefreshFeedCollection'
import { ExploreFeedsDialog } from '../ExploreFeedsDialog/ExploreFeedsDialog'
import { ExploreFeedsDialogDropdownMenuItem } from '../ExploreFeedsDialog/ExploreFeedsDialogDropdownMenuItem'
import {
  FeedArticleCardSkeleton,
  FeedArticleLoadFailure,
} from '../FeedArticleList/FeedArticleCard'
import { FeedArticleList } from '../FeedArticleList/FeedArticleList'
import { ImportFeedsDialogDropdownMenuItem } from '../ImportFeedsDialog/ImportFeedsDialogDropdownMenuItem'
import { ListHeaderTitle } from '../ListHeader/ListHeaderTitle'
import { SubscribeToFeedDialogDropdownMenuItem } from '../SubscribeToFeedDialog/SubscribeToFeedDialogDropdownMenuItem'

import styles from './CollectionList.module.css'

const CollectionListCollection = graphql(/* GraphQL */ `
  fragment CollectionListCollection on FeedCollection {
    id
    name
    feeds {
      id
      name
      lastRefreshed
      connectionMetadata {
        ... on RSSFeedConnectionMetadata {
          __typename
          url
        }
        ... on NewsSearchConnectionMetadata {
          __typename
          query
        }
      }
    }
  }
`)
export const GetCollectionListDetails = graphql(
  /* GraphQL */ `
    query GetCollectionListDetails($feedCollectionId: FeedCollectionId!) {
      feedCollection(input: { id: $feedCollectionId }) {
        ...CollectionListCollection
      }
    }
  `,
  [CollectionListCollection],
)

export const CollectionList = (): JSX.Element => {
  const { feedCollectionId } = useParams<{ feedCollectionId: string }>()
  const organizationId = useOrganizationId()
  const navigate = useNavigate()
  const isMobileScreenSize = useIsMobileScreenSize()
  const [refreshFeedCollection] = useRefreshFeedCollection()
  const pageLayoutScrollableRef = useInMemoryRestoreScroll(
    feedCollectionId ?? '',
  )
  const {
    data,
    loading: loadingCollectionData,
    error: collectionListError,
  } = useQuery(GetCollectionListDetails, {
    variables: {
      feedCollectionId: feedCollectionId ?? '',
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  })

  const collection = readFragment(
    CollectionListCollection,
    data?.feedCollection,
  )

  const {
    loading: loadingFeedItems,
    items,
    fetchingMore,
    refetch,
    lastElementRef,
    error: feedItemsError,
  } = useFeedItems({
    organizationId,
    feedCollectionId,
  })

  // We need this to make sure the Empty state stays rendered while feeds are being
  // added inside of its dialogs, otherwise as soon as one feed is added the empty state
  // and dialog will stop being rendered
  const [isAddingFeeds, setIsAddingFeeds] = useState(false)
  const shouldRenderEmptyState =
    (!loadingCollectionData && !collection?.feeds?.length) || isAddingFeeds
  const handleEmptyStateDialogOpenChange = (open: boolean): void => {
    setIsAddingFeeds(open)
  }

  usePageTitle(collection?.name ? `${collection.name} (Feed)` : 'Feeds')

  const feeds = useMemo(
    () =>
      collection?.feeds.map((feed) => ({
        id: feed.id,
        name: feed.name,
        url: isOfType(feed.connectionMetadata, 'RSSFeedConnectionMetadata')
          ? feed.connectionMetadata?.url
          : undefined,
      })) ?? [],
    [collection?.feeds],
  )
  const existingFeeds = feeds
    .map((feed) => (feed.url ? { id: feed.id, url: feed.url } : undefined))
    .filter(isNotUndefined)

  /**
   * Calculate limits
   */
  const { account } = useAccount()
  const tier = isFreeUser(account) ? 'free' : 'paid'

  // Get the total number of feeds for the organization with cache-first to calculate remaining feeds
  const { totalFeeds } = useFeedsForSidebar({
    organizationId,
    fetchPolicy: 'cache-first',
  })
  const totalRemainingFeeds = Limits.feeds[tier].total - totalFeeds
  const collectionRemainingFeeds =
    Limits.feeds[tier].perCollection - feeds.length
  // If the total remaining feeds is less than the collection remaining feeds, use the total remaining feeds
  const remainingFeedsCount =
    collectionRemainingFeeds < totalRemainingFeeds
      ? collectionRemainingFeeds
      : totalRemainingFeeds

  // If the collection limit has not been reached, but the total feed limit has,
  // we still have to prevent adding a feed to the collection, so we have to
  // check which limit has been reached to properly inform the limit dialogs
  const totalLimitReached = totalFeeds >= Limits.feeds[tier].total
  const collectionLimitReached =
    feeds.length >= Limits.feeds[tier].perCollection
  const limitReachedType = collectionLimitReached
    ? 'feedspercollection'
    : 'feeds'

  /**
   * Event handlers
   */
  const isOneBufferOrg = useIsOneBufferOrg()
  const hasPaymentDetails = useHasPaymentDetails()
  const commonTrackingProperties = useCommonTrackingProperties()
  const baseTrackingProperties = useMemo(
    () => ({
      clientName: Product.Publish,
      product: Client.PublishWeb,
      hasPaymentDetails,
      isFreeUser: tier === 'free',
      ...commonTrackingProperties,
    }),
    [commonTrackingProperties, hasPaymentDetails, tier],
  )

  const handleUpgradeClick = ({
    cta,
    upgradePathName,
  }: {
    cta: CTA
    upgradePathName: string
  }): void => {
    const trackingProperties = {
      ...baseTrackingProperties,
      cta,
      upgradePathName,
    }
    if (isOneBufferOrg) {
      BufferTrackerReact.cTAClicked(trackingProperties)

      const { MODALS, actions } = window?.appshell || {}
      actions.openModal(MODALS.planSelector, trackingProperties)
    } else {
      navigateToMigrationHub(cta)
    }
  }

  const [createFeed] = useCreateFeed()
  const handleCreateFeed = useCallback(
    async ({
      feedUrl,
      cta,
    }: {
      feedUrl: string
      cta: FeedCTA
    }): Promise<WrappedCreateFeedResponse> => {
      if (!organizationId) {
        throw new Error('Organization ID is required')
      }

      const result = await createFeed({
        variables: {
          input: {
            organizationId,
            collectionId: collection?.id,
            rssAtom: { url: feedUrl },
          },
        },
      })

      if (result.success) {
        toast.success('Successfully added feed')
        BufferTrackerReact.feedSubscribed({
          product: 'publish',
          organizationId,
          feedId: result.data.feed.id,
          feedCollectionId: collection?.id ?? '',
          feedUrl,
          feedQuery: undefined,
          feedType: 'rssAtom',
          clientName: 'publishWeb',
          cta,
        })
        refetch()
        return { feedId: result.data.feed.id, error: null }
      }
      return { feedId: null, error: parseErrorMessage(result.error) }
    },
    [createFeed, organizationId, refetch, collection?.id],
  )

  const [deleteFeed] = useDeleteFeed()
  const handleDeleteFeed = useCallback(
    async (deletingFeedId: string) => {
      if (!organizationId) return

      const { error } = await deleteFeed({
        variables: { input: { organizationId, id: deletingFeedId } },
      })

      if (error) {
        toast.error(`Failed to delete feed`)
        return
      }

      // Only redirect if the deleted feed is the active one
      if (!location.pathname.includes(deletingFeedId)) {
        return
      }
      if (collection?.id) {
        navigate('/create/feed/collections/' + collection.id)
        return
      }
      navigate('/create')
    },
    [deleteFeed, collection, organizationId, navigate],
  )

  const loadingState = useMemo(() => {
    if (collectionListError || feedItemsError)
      return {
        state: 'error',
        error: collectionListError || feedItemsError,
      } as const
    if (shouldRenderEmptyState) return { state: 'empty' } as const
    if ((loadingFeedItems || loadingCollectionData) && !items?.length)
      return { state: 'loading' } as const
    if (items && items.length > 0) return { state: 'loaded' } as const
    return { state: 'loading' } as const
  }, [
    shouldRenderEmptyState,
    loadingFeedItems,
    loadingCollectionData,
    items,
    collectionListError,
    feedItemsError,
  ])

  const lastUpdated = useMemo(() => {
    const timestamps = collection?.feeds.map((feed) => {
      return new Date(feed.lastRefreshed)
    })
    if (!timestamps) {
      return new Date()
    }
    timestamps.sort((a, b) => a.getTime() - b.getTime())
    return timestamps.length > 0 ? timestamps[0] : null
  }, [collection?.feeds])

  return (
    <>
      <Outlet />
      <PageLayout className={styles.pageLayout} ref={pageLayoutScrollableRef}>
        <PageLayout.Header>
          <PageLayout.HeaderRow>
            <ListHeaderTitle
              title={collection?.name ?? ''}
              isCollection
              isEmptyCollection={!collection?.feeds?.length}
              isLoading={!collection}
              lastUpdated={lastUpdated}
              onRefresh={async (): Promise<{
                success: boolean
                message: string
              }> => {
                if (!collection || !organizationId) {
                  return {
                    success: false,
                    message: 'Collection not found',
                  }
                }
                try {
                  const { error } = await refreshFeedCollection({
                    variables: {
                      input: {
                        feedCollectionId: collection.id,
                        organizationId,
                      },
                    },
                  })
                  if (!error) {
                    return {
                      success: true,
                      message: 'Your collection has been refreshed!',
                    }
                  }
                  // eslint-disable-next-line unused-imports/no-unused-vars
                } catch (_error) {
                  // Do nothing, generic error shown below
                }
                return {
                  success: false,
                  message: 'Failed to refresh collection',
                }
              }}
            />
            <PageLayout.Actions>
              <FeedbackWidget id="content-feeds-1" source="create">
                <Button variant="tertiary" size="large">
                  <MessageCircleHeartIcon />
                  Share Feedback
                </Button>
              </FeedbackWidget>
              {!isMobileScreenSize && (
                <DropdownMenu>
                  <DropdownMenu.Trigger>
                    <Button size="large" variant="secondary">
                      <PlusIcon />
                      Add Feed
                    </Button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content>
                    <SubscribeToFeedDialogDropdownMenuItem
                      existingFeeds={existingFeeds}
                      tier={tier}
                      type={limitReachedType}
                      limitReached={collectionLimitReached || totalLimitReached}
                      onAddFeed={handleCreateFeed}
                      onOpenChange={handleEmptyStateDialogOpenChange}
                      onUpgrade={handleUpgradeClick}
                    />
                    <ImportFeedsDialogDropdownMenuItem
                      existingFeeds={existingFeeds}
                      remainingFeedsCount={remainingFeedsCount}
                      tier={tier}
                      type={limitReachedType}
                      limitReached={collectionLimitReached || totalLimitReached}
                      onAddFeed={handleCreateFeed}
                      onRemoveFeed={handleDeleteFeed}
                      onOpenChange={handleEmptyStateDialogOpenChange}
                      onUpgrade={handleUpgradeClick}
                    />
                    <DropdownMenu.Separator />
                    <ExploreFeedsDialogDropdownMenuItem
                      existingFeeds={existingFeeds}
                      tier={tier}
                      type={limitReachedType}
                      limitReached={collectionLimitReached || totalLimitReached}
                      onAddFeed={handleCreateFeed}
                      onRemoveFeed={handleDeleteFeed}
                      onUpgrade={handleUpgradeClick}
                    />
                  </DropdownMenu.Content>
                </DropdownMenu>
              )}
            </PageLayout.Actions>
          </PageLayout.HeaderRow>
        </PageLayout.Header>
        <PageLayout.Container size="narrow">
          {loadingState.state === 'empty' && (
            <CollectionListEmptyState>
              <ExploreFeedsDialog
                existingFeeds={existingFeeds}
                tier={tier}
                type={limitReachedType}
                limitReached={collectionLimitReached || totalLimitReached}
                onAddFeed={handleCreateFeed}
                onRemoveFeed={handleDeleteFeed}
                onOpenChange={handleEmptyStateDialogOpenChange}
                onUpgrade={(): void =>
                  handleUpgradeClick({
                    cta: 'create-feeds-upgradeDialog-upgradeButton-1',
                    upgradePathName: 'feeds-exploreFeeds-upgrade',
                  })
                }
              >
                <Button variant="secondary" size="large">
                  <CompassIcon /> Explore Feeds
                </Button>
              </ExploreFeedsDialog>
              <DropdownMenu>
                <DropdownMenu.Trigger>
                  <Button size="large" variant="primary">
                    <PlusIcon />
                    Add Feed
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content>
                  <SubscribeToFeedDialogDropdownMenuItem
                    existingFeeds={existingFeeds}
                    tier={tier}
                    type={limitReachedType}
                    buttonText="Add from URL"
                    limitReached={collectionLimitReached || totalLimitReached}
                    onAddFeed={handleCreateFeed}
                    onOpenChange={handleEmptyStateDialogOpenChange}
                    onUpgrade={handleUpgradeClick}
                  />
                  <ImportFeedsDialogDropdownMenuItem
                    existingFeeds={existingFeeds}
                    remainingFeedsCount={remainingFeedsCount}
                    tier={tier}
                    type={limitReachedType}
                    limitReached={collectionLimitReached || totalLimitReached}
                    onAddFeed={handleCreateFeed}
                    onRemoveFeed={handleDeleteFeed}
                    onOpenChange={handleEmptyStateDialogOpenChange}
                    onUpgrade={handleUpgradeClick}
                  />
                </DropdownMenu.Content>
              </DropdownMenu>
            </CollectionListEmptyState>
          )}
          {loadingState.state === 'loading' &&
            range(20).map((index) => <FeedArticleCardSkeleton key={index} />)}
          {loadingState.state === 'loaded' && (
            <FeedArticleList
              feedCollectionId={feedCollectionId}
              items={items}
              fetchingMore={fetchingMore}
              lastElementRef={lastElementRef}
            />
          )}
          {loadingState.state === 'error' && <FeedArticleLoadFailure />}
        </PageLayout.Container>
      </PageLayout>
    </>
  )
}
